import { WeaponConfig, WeaponType } from "mmo-common";
import { Render3DGameObject } from "realms-engine-browser";
import { disposeThreeObject } from "realms-three-utils";
import { MMOAssetManager } from "@game/config";
import {
  LerpGameObject,
  ProcessGameObject,
  SmoothDampVector3,
  Vector3,
  vector3Add,
  vector3DistanceTo,
} from "realms-engine";
import { FLOATING_WEAPON_MAPPING } from "./mapping";
import { createDissolveMaterial } from "@game/materials/dissolveMaterial";
import THREE from "realms-three";
import {
  WEAPON_ASSET_MAPPING,
  WEAPON_TEXTURE_ASSET_MAPPING,
  WeaponTextureAssetConfig,
} from "../base-character/mappings/weapon-mapping";
import { CHARACTER_SCALE } from "@game/config/assetMaps/base-character";

export class FloatingWeaponGameObject extends Render3DGameObject {
  processGameObject: ProcessGameObject;

  private weapon: WeaponConfig<WeaponType> | null;
  private weaponMesh: THREE.Mesh | null = null;
  private target: Vector3 | null = null;
  private targetDamping: SmoothDampVector3;
  private targetSmoothTime: number;
  private dampingMaxSpeed: number;
  private weaponDissolveTimeMs: number;

  private updateDissolveMaterial:
    | ReturnType<typeof createDissolveMaterial>["updateUniforms"]
    | null = null;

  private dissolveUniformLerp: LerpGameObject<number>;

  constructor({
    weapon,
    targetSmoothTime = 200,
    dampingMaxSpeed = 100,
    weaponDissolveTimeMs = 800,
  }: {
    weapon: WeaponConfig<WeaponType> | null;
    targetSmoothTime?: number;
    dampingMaxSpeed?: number;
    weaponDissolveTimeMs?: number;
  }) {
    super();
    this.weaponDissolveTimeMs = weaponDissolveTimeMs;
    this.processGameObject = this.addChild(
      new ProcessGameObject({}, this.onStep)
    );
    this.setWeapon(weapon);
    this.weapon = weapon;
    this.getThreeObject().scale.setScalar(CHARACTER_SCALE);
    this.targetSmoothTime = targetSmoothTime;
    this.dampingMaxSpeed = dampingMaxSpeed;
    this.targetDamping = new SmoothDampVector3(
      () => this.getPosition(),
      this.dampingMaxSpeed
    );
    this.dissolveUniformLerp = this.addChild(
      new LerpGameObject<number>((progress) =>
        this.updateDissolveMaterial?.({ progress })
      )
    );
  }

  private onStep = (delta: number) => {
    if (!this.target || !this.weapon) {
      return;
    }
    const { position: offset } =
      FLOATING_WEAPON_MAPPING[this.weapon.weaponType].transformation;

    const nextPosition = this.targetDamping.damp(
      vector3Add(this.target, offset),
      this.targetSmoothTime,
      delta
    );

    const distance = vector3DistanceTo(nextPosition, this.getPosition());
    if (distance > 0.02) {
      this.setPosition(nextPosition);
    }
  };

  setTarget(target: Vector3 | null) {
    this.target = target;
  }

  /**
   * Sets weapon visibility
   */
  setWeaponVisibility(visibility: boolean, options: { delay?: number } = {}) {
    if (!this.weaponMesh) {
      return;
    }
    if (this.weaponMesh.visible === visibility) {
      return;
    }
    this.weaponMesh.visible = visibility;
    if (visibility) {
      this.dissolveUniformLerp.lerp(0, 1, this.weaponDissolveTimeMs, options);
    } else {
      this.dissolveUniformLerp.lerp(1, 0, this.weaponDissolveTimeMs, options);
    }
  }

  /**
   * Sets the weapon
   */
  setWeapon<T extends WeaponType>(weapon: WeaponConfig<T> | null) {
    // No change so ignore
    if (
      weapon?.texture === this.weapon?.texture &&
      weapon?.weaponType === this.weapon?.weaponType
    ) {
      return;
    }

    this.weapon = weapon;
    if (this.weaponMesh) {
      this.getThreeObject().remove(this.weaponMesh);
      disposeThreeObject(this.weaponMesh);
      this.weaponMesh = null;
    }

    const weaponConfig = weapon?.weaponType
      ? WEAPON_ASSET_MAPPING[weapon.weaponType]
      : undefined;

    const weaponTextureConfig: WeaponTextureAssetConfig | undefined =
      weapon?.weaponType
        ? (WEAPON_TEXTURE_ASSET_MAPPING[weapon.weaponType] as any)[
            weapon.texture
          ]
        : undefined;

    // No weapon just remove
    if (weapon === null || !weaponConfig || !weaponTextureConfig) {
      return;
    }

    // Add weapon to mesh
    this.weaponMesh = MMOAssetManager.getMesh(weaponConfig.asset, {
      cloneMaterial: true,
    });

    const weaponMaterial = this.weaponMesh.material as THREE.MeshPhongMaterial;
    weaponMaterial.map = MMOAssetManager.getTexture(weaponTextureConfig.asset, {
      clone: true,
    });
    weaponMaterial.transparent = Boolean(weaponTextureConfig.transparent);

    const { material, updateUniforms } = createDissolveMaterial({
      baseMaterial: this.weaponMesh.material as THREE.Material,
      progress: 1,
    });

    this.weaponMesh.material = material;
    this.weaponMesh.material.side = THREE.DoubleSide;
    this.updateDissolveMaterial = updateUniforms;
    const { rotation } =
      FLOATING_WEAPON_MAPPING[weapon.weaponType].transformation;
    this.weaponMesh.rotation.set(rotation.x, rotation.y, rotation.z);
    this.getThreeObject().add(this.weaponMesh);
  }
}
